import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  Badge,
  Icon,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  InputGroup,
  InputRightElement,
  Textarea,
  Checkbox,
  SimpleGrid,
  Divider,
  Stack,
  Switch,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useDisclosure,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import {
  FiPlus,
  FiEdit,
  FiSend,
  FiSlash,
  FiClock,
  FiZap,
  FiX,
} from 'react-icons/fi'
import { DataTable } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import type {
  ColumnConfig,
  ActionButton,
  FilterConfig,
  PaginationConfig,
} from '@/components/common/DataTable/types'
import { useToast } from '@/hooks/useToast'
import {
  useAnnouncements,
  useAnnouncement,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  usePublishAnnouncement,
  useRevokeAnnouncement,
} from '@/hooks/useAdminData'
import type {
  Announcement,
  AnnouncementChannels,
  AnnouncementDetail,
  AnnouncementSeverity,
  AnnouncementStatus,
  BarContentSegment,
} from '@/types'
import { formatDate, formatTime, formatDateTime } from '@/utils/formatUtils'
import { useAuth } from '@/contexts/AuthContext'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

type DrawerMode = 'create' | 'edit'

type AnnouncementFormState = {
  title: string
  emailSubtitle: string
  content: string
  severity: AnnouncementSeverity
  channels: AnnouncementChannels
  barSegments: BarContentSegment[]
  ctaLabel: string
  ctaUrl: string
  emailContentBlocks: string
  startsAt: string
  endsAt: string
  schedule: boolean
}

const ANNOUNCEMENT_TITLE_MAX_LENGTH = 60
const ANNOUNCEMENT_EMAIL_SUBTITLE_MAX_LENGTH = 120
const ANNOUNCEMENT_CONTENT_MAX_LENGTH = 1200
const ANNOUNCEMENT_BAR_TOTAL_MAX_LENGTH = 140

const toLocalDateTimeInput = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const pad = (num: number) => num.toString().padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const toIsoStringFromLocal = (value: string): string | null | undefined => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }
  return date.toISOString()
}

const formatUtcHint = (
  value: string,
  t: (key: string, options?: Record<string, string>) => string,
): string => {
  const iso = toIsoStringFromLocal(value)
  if (iso === undefined) {
    return t('announcements.publish.utcInvalid')
  }
  if (!iso) {
    return t('announcements.publish.utcPlaceholder')
  }
  return t('announcements.publish.utcTime', { time: iso })
}

const buildEmptyForm = (): AnnouncementFormState => ({
  title: '',
  emailSubtitle: '',
  content: '',
  severity: 'notice',
  channels: {
    modal: true,
    bar: false,
    email: false,
  },
  barSegments: [],
  ctaLabel: 'Learn more',
  ctaUrl: '',
  emailContentBlocks: '',
  startsAt: '',
  endsAt: '',
  schedule: false,
})

const buildFormFromAnnouncement = (announcement: Announcement | null): AnnouncementFormState => {
  if (!announcement) {
    return buildEmptyForm()
  }
  return {
    title: announcement.title ?? '',
    emailSubtitle: announcement.emailSubtitle ?? '',
    content: announcement.content ?? '',
    severity: announcement.severity ?? 'notice',
    channels: announcement.channels ?? { modal: false, bar: false, email: false },
    barSegments: announcement.barContent ?? [],
    ctaLabel: announcement.ctaLabel ?? '',
    ctaUrl: announcement.ctaUrl ?? '',
    emailContentBlocks: announcement.emailContentBlocks ?? '',
    startsAt: toLocalDateTimeInput(announcement.startsAt),
    endsAt: toLocalDateTimeInput(announcement.endsAt),
    schedule: false,
  }
}

const normalizeOptionalString = (value: string): string | null => {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const normalizeSegments = (
  segments: BarContentSegment[],
  t: (key: string) => string,
): { normalized: BarContentSegment[]; error?: string } => {
  if (segments.length > 20) {
    return { normalized: [], error: t('announcements.validation.barSegmentLimit') }
  }

  const normalized: BarContentSegment[] = []
  let totalLength = 0

  for (const segment of segments) {
    const text = segment.text?.trim() ?? ''
    if (!text) {
      return { normalized: [], error: t('announcements.validation.barSegmentTextRequired') }
    }
    if (text.length > 120) {
      return { normalized: [], error: t('announcements.validation.barSegmentTooLong') }
    }
    totalLength += text.length
    if (totalLength > ANNOUNCEMENT_BAR_TOTAL_MAX_LENGTH) {
      return { normalized: [], error: t('announcements.validation.barTotalTooLong') }
    }

    if (segment.type === 'link') {
      const url = segment.url?.trim() ?? ''
      if (!url) {
        return { normalized: [], error: t('announcements.validation.barLinkRequired') }
      }
      if (!url.startsWith('https://')) {
        return { normalized: [], error: t('announcements.validation.barLinkHttps') }
      }
      if (url.length > 2048) {
        return { normalized: [], error: t('announcements.validation.barLinkTooLong') }
      }
      normalized.push({ type: 'link', text, url })
      continue
    }

    normalized.push({ type: 'text', text })
  }

  return { normalized }
}

export function AnnouncementsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const drawer = useDisclosure()
  const drawerBg = useColorModeValue('white', 'gray.800')
  const { checkPermission } = useAuth()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [statusFilter, setStatusFilter] = useState('all')
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('create')
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [formState, setFormState] = useState<AnnouncementFormState>(buildEmptyForm())
  const preserveScheduleRef = useRef(false)

  const listParams = {
    status: statusFilter === 'all' ? undefined : statusFilter,
    page,
    limit: pageSize,
  }
  const {
    data: listData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAnnouncements(listParams)

  const detailQuery = useAnnouncement(selectedAnnouncement?.id, {
    enabled: drawer.isOpen && drawerMode === 'edit',
  })

  const canManage = checkPermission('system:config')

  const createMutation = useCreateAnnouncement()
  const updateMutation = useUpdateAnnouncement()
  const publishMutation = usePublishAnnouncement()
  const revokeMutation = useRevokeAnnouncement()

  const activeAnnouncement = detailQuery.data ?? selectedAnnouncement
  const announcementStatus = activeAnnouncement?.status ?? 'draft'
  const isPublished = announcementStatus === 'published'
  const isReadOnly = announcementStatus === 'revoked' || announcementStatus === 'expired'

  useEffect(() => {
    if (!drawer.isOpen) {
      return
    }
    if (drawerMode === 'create') {
      setFormState(buildEmptyForm())
      return
    }
    const nextForm = buildFormFromAnnouncement(selectedAnnouncement)
    if (preserveScheduleRef.current) {
      setFormState((prev) => ({ ...nextForm, schedule: prev.schedule }))
      preserveScheduleRef.current = false
      return
    }
    setFormState(nextForm)
  }, [drawer.isOpen, drawerMode, selectedAnnouncement?.id])

  const statusOptions: FilterConfig[] = [
    {
      key: 'status',
      type: 'select',
      label: t('announcements.filters.status'),
      placeholder: t('announcements.filters.statusPlaceholder'),
      width: '220px',
      options: [
        { value: 'all', label: t('common.all') },
        { value: 'draft', label: t('announcements.statuses.draft') },
        { value: 'scheduled', label: t('announcements.statuses.scheduled') },
        { value: 'published', label: t('announcements.statuses.published') },
        { value: 'expired', label: t('announcements.statuses.expired') },
        { value: 'revoked', label: t('announcements.statuses.revoked') },
      ],
    },
  ]

  const paginationConfig: PaginationConfig = {
    page,
    pageSize,
    total: listData?.total ?? 0,
    pageSizeOptions: [20, 50, 100],
  }

  const totalBarLength = useMemo(() => {
    return formState.barSegments.reduce((total, segment) => total + (segment.text?.length ?? 0), 0)
  }, [formState.barSegments])

  const getStatusBadgeColor = (status: AnnouncementStatus) => {
    switch (status) {
      case 'draft':
        return 'gray'
      case 'scheduled':
        return 'purple'
      case 'published':
        return 'green'
      case 'expired':
        return 'orange'
      case 'revoked':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getSeverityBadgeColor = (severity: AnnouncementSeverity) => {
    return severity === 'urgent' ? 'red' : 'blue'
  }

  const columns: ColumnConfig<Announcement>[] = [
    {
      key: 'title',
      title: t('announcements.fields.title'),
      width: '24%',
      render: (value) => (
        <Box>
          <Text fontSize="sm" fontWeight="medium">
            {value}
          </Text>
        </Box>
      ),
    },
    {
      key: 'status',
      title: t('announcements.filters.status'),
      width: '12%',
      render: (value) => (
        <Badge colorScheme={getStatusBadgeColor(value as AnnouncementStatus)}>
          {t(`announcements.statuses.${value}`)}
        </Badge>
      ),
    },
    {
      key: 'severity',
      title: t('announcements.fields.severity'),
      width: '10%',
      render: (value) => (
        <Badge colorScheme={getSeverityBadgeColor(value as AnnouncementSeverity)}>
          {t(`announcements.severityLabels.${value}`)}
        </Badge>
      ),
    },
    {
      key: 'channels',
      title: t('announcements.fields.channels'),
      width: '16%',
      render: (_value, row) => (
        <HStack spacing={2} wrap="wrap">
          {row.channels?.modal && (
            <Badge colorScheme="teal">{t('announcements.channelLabels.modal')}</Badge>
          )}
          {row.channels?.bar && (
            <Badge colorScheme="cyan">{t('announcements.channelLabels.bar')}</Badge>
          )}
          {row.channels?.email && (
            <Badge colorScheme="purple">{t('announcements.channelLabels.email')}</Badge>
          )}
        </HStack>
      ),
    },
    {
      key: 'startsAt',
      title: t('announcements.fields.startsAt'),
      width: '16%',
      render: (value, row) => (
        <Box>
          <Text fontSize="sm">
            {value ? formatDate(value) : t('common.na')}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {value ? formatTime(value) : ''}
          </Text>
          {row.endsAt && (
            <Text fontSize="xs" color="gray.500">
              {t('announcements.fields.endsAt')}: {formatDateTime(row.endsAt)}
            </Text>
          )}
        </Box>
      ),
    },
    {
      key: 'updatedAt',
      title: t('common.updated'),
      width: '12%',
      render: (value) => (
        <Box>
          <Text fontSize="sm">{value ? formatDate(value) : t('common.na')}</Text>
          <Text fontSize="xs" color="gray.500">
            {value ? formatTime(value) : ''}
          </Text>
        </Box>
      ),
    },
  ]

  const handleFiltersChange = (filters: Record<string, any>) => {
    setStatusFilter(filters.status || 'all')
    setPage(1)
  }

  const handleOpenCreate = () => {
    setSelectedAnnouncement(null)
    setDrawerMode('create')
    drawer.onOpen()
  }

  const handleOpenEdit = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement)
    setDrawerMode('edit')
    drawer.onOpen()
  }

  const handleCloseDrawer = () => {
    setSelectedAnnouncement(null)
    drawer.onClose()
  }

  const handleToggleChannel = (channel: keyof AnnouncementChannels) => {
    setFormState((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: !prev.channels[channel],
      },
    }))
  }

  const handleSegmentChange = (index: number, key: 'type' | 'text' | 'url', value: string) => {
    setFormState((prev) => {
      const nextSegments = [...prev.barSegments]
      const target = { ...nextSegments[index] }
      if (key === 'type' && value === 'text') {
        delete (target as { url?: string }).url
      }
      if (key === 'type') {
        target.type = value as 'text' | 'link'
      } else if (key === 'text') {
        target.text = value
      } else if (key === 'url') {
        const targetWithUrl = target as { url?: string }
        targetWithUrl.url = value
      }
      nextSegments[index] = target as BarContentSegment
      return { ...prev, barSegments: nextSegments }
    })
  }

  const handleAddSegment = () => {
    if (formState.barSegments.length >= 20) {
      toast.error(t('announcements.validation.barSegmentLimit'))
      return
    }
    setFormState((prev) => ({
      ...prev,
      barSegments: [...prev.barSegments, { type: 'text', text: '' }],
    }))
  }

  const handleRemoveSegment = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      barSegments: prev.barSegments.filter((_, idx) => idx !== index),
    }))
  }

  const validateForm = (
    mode: 'create' | 'update' | 'publish',
    channels: AnnouncementChannels,
    state: AnnouncementFormState = formState,
  ): { payload?: Record<string, unknown>; error?: string } => {
    const title = state.title.trim()
    if (mode !== 'publish' && !title) {
      return { error: t('announcements.validation.titleRequired') }
    }
    if (mode !== 'publish' && title.length > ANNOUNCEMENT_TITLE_MAX_LENGTH) {
      return {
        error: t('announcements.validation.titleTooLong', {
          max: ANNOUNCEMENT_TITLE_MAX_LENGTH,
        }),
      }
    }

    const emailSubtitle = state.emailSubtitle.trim()
    if (
      mode !== 'publish' &&
      emailSubtitle &&
      emailSubtitle.length > ANNOUNCEMENT_EMAIL_SUBTITLE_MAX_LENGTH
    ) {
      return {
        error: t('announcements.validation.emailSubtitleTooLong', {
          max: ANNOUNCEMENT_EMAIL_SUBTITLE_MAX_LENGTH,
        }),
      }
    }

    const content = state.content.trim()
    if (mode !== 'publish' && content && content.length > ANNOUNCEMENT_CONTENT_MAX_LENGTH) {
      return {
        error: t('announcements.validation.contentTooLong', {
          max: ANNOUNCEMENT_CONTENT_MAX_LENGTH,
        }),
      }
    }

    if (mode !== 'publish' && !(mode === 'update' && isPublished)) {
      const hasChannel = channels.modal || channels.bar || channels.email
      if (!hasChannel) {
        return { error: t('announcements.validation.channelRequired') }
      }
      if (channels.modal && !state.content.trim()) {
        return { error: t('announcements.validation.contentRequired') }
      }
    }

    let normalizedSegments: BarContentSegment[] = []
    if (channels.bar && !(mode === 'update' && isPublished)) {
      const normalized = normalizeSegments(state.barSegments, t)
      if (normalized.error) {
        return { error: normalized.error }
      }
      normalizedSegments = normalized.normalized
      if (normalizedSegments.length === 0) {
        return { error: t('announcements.validation.barRequired') }
      }
    }

    if (
      mode !== 'publish' &&
      !(mode === 'update' && isPublished) &&
      state.ctaUrl.trim() &&
      !state.ctaUrl.trim().startsWith('https://')
    ) {
      return { error: t('announcements.validation.ctaLinkHttps') }
    }

    const shouldValidateDates = mode !== 'publish' || state.schedule
    const startsAtIso = shouldValidateDates
      ? toIsoStringFromLocal(state.startsAt)
      : null
    if (startsAtIso === undefined) {
      return { error: t('announcements.validation.invalidDate') }
    }
    const endsAtIso = shouldValidateDates
      ? toIsoStringFromLocal(state.endsAt)
      : null
    if (endsAtIso === undefined) {
      return { error: t('announcements.validation.invalidDate') }
    }
    if (startsAtIso && endsAtIso) {
      const startsAtMs = new Date(startsAtIso).getTime()
      const endsAtMs = new Date(endsAtIso).getTime()
      if (endsAtMs <= startsAtMs) {
        return { error: t('announcements.validation.endBeforeStart') }
      }
    }

    const payload: Record<string, unknown> = {
      title,
      email_subtitle: normalizeOptionalString(state.emailSubtitle),
      content: normalizeOptionalString(state.content),
      bar_content: normalizedSegments.length > 0 ? normalizedSegments : null,
      severity: state.severity,
      channels,
      starts_at: startsAtIso,
      ends_at: endsAtIso,
      cta_label: normalizeOptionalString(state.ctaLabel),
      cta_url: normalizeOptionalString(state.ctaUrl),
      email_content_blocks: normalizeOptionalString(state.emailContentBlocks),
      schedule: state.schedule,
    }

    if (mode === 'publish') {
      const publishPayload: Record<string, unknown> = {
        schedule: state.schedule,
      }
      if (state.schedule && startsAtIso !== null) {
        publishPayload.starts_at = startsAtIso
      }
      if (state.schedule && endsAtIso !== null) {
        publishPayload.ends_at = endsAtIso
      }
      return { payload: publishPayload }
    }

    if (mode === 'update' && isPublished) {
      return {
        payload: {
          ends_at: endsAtIso,
        },
      }
    }

    return { payload }
  }

  const handleCreate = async () => {
    const { payload, error: validationError } = validateForm('create', formState.channels)
    if (validationError) {
      toast.error(validationError)
      return
    }

    try {
      const created = await createMutation.mutateAsync(payload ?? {})
      toast.success(t('common.success'))
      preserveScheduleRef.current = true
      setSelectedAnnouncement(created)
      setDrawerMode('edit')
    } catch (err) {
      toast.error(err as Error)
    }
  }

  const handleUpdate = async () => {
    if (!selectedAnnouncement?.id) {
      return
    }
    const { payload, error: validationError } = validateForm('update', formState.channels)
    if (validationError) {
      toast.error(validationError)
      return
    }

    try {
      const updated = await updateMutation.mutateAsync({
        id: selectedAnnouncement.id,
        payload: payload ?? {},
      })
      toast.success(t('common.success'))
      setSelectedAnnouncement(updated)
    } catch (err) {
      toast.error(err as Error)
    }
  }

  const handlePublish = async (announcement?: Announcement) => {
    const target = announcement ?? selectedAnnouncement
    if (!target?.id) {
      return
    }
    if (announcement) {
      try {
        const published = await publishMutation.mutateAsync({
          id: target.id,
          payload: {},
        })
        toast.success(t('announcements.actions.publish'))
        setSelectedAnnouncement(published)
      } catch (err) {
        toast.error(err as Error)
      }
      return
    }

    const channels = target.channels ?? formState.channels
    const { payload, error: validationError } = validateForm('publish', channels)
    if (validationError) {
      toast.error(validationError)
      return
    }

    try {
      const published = await publishMutation.mutateAsync({
        id: target.id,
        payload: payload ?? {},
      })
      toast.success(t('announcements.actions.publish'))
      setSelectedAnnouncement(published)
    } catch (err) {
      toast.error(err as Error)
    }
  }

  const handleRevoke = async (announcement: Announcement) => {
    try {
      const revoked = await revokeMutation.mutateAsync(announcement.id)
      toast.success(t('announcements.actions.revoke'))
      if (selectedAnnouncement?.id === announcement.id) {
        setSelectedAnnouncement(revoked)
      }
    } catch (err) {
      toast.error(err as Error)
    }
  }

  const getRowActions = (announcement: Announcement): ActionButton[] => {
    const actions: ActionButton[] = [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => handleOpenEdit(announcement),
        size: 'xs',
        variant: 'ghost',
      },
    ]

    if (announcement.status === 'draft' || announcement.status === 'scheduled') {
      actions.push({
        label: t('announcements.actions.publishNow'),
        icon: <FiSend />,
        onClick: () => handlePublish(announcement),
        size: 'xs',
        variant: 'ghost',
        confirmMessage: t('announcements.confirm.publishNow'),
      })
    }

    if (announcement.status === 'published' || announcement.status === 'scheduled') {
      actions.push({
        label: t('announcements.actions.revoke'),
        icon: <FiSlash />,
        onClick: () => handleRevoke(announcement),
        size: 'xs',
        variant: 'ghost',
        colorScheme: 'red',
        confirmMessage: t('announcements.confirm.revoke'),
      })
    }

    return actions
  }

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage)
  }

  const handlePageSizeChange = (nextSize: number) => {
    setPageSize(nextSize)
    setPage(1)
  }

  const handleFormChange = useCallback(
    (key: keyof AnnouncementFormState) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value
        setFormState((prev) => ({ ...prev, [key]: value }))
      },
    []
  )

  const handleSeverityChange = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      severity: value as AnnouncementSeverity,
    }))
  }

  const handleScheduleToggle = () => {
    setFormState((prev) => ({ ...prev, schedule: !prev.schedule }))
  }

  const handleStatusSummary = (announcement?: AnnouncementDetail | null) => {
    if (!announcement) {
      return null
    }
    return (
      <HStack spacing={3}>
        <Badge colorScheme={getStatusBadgeColor(announcement.status)}>
          {t(`announcements.statuses.${announcement.status}`)}
        </Badge>
        <Badge colorScheme={getSeverityBadgeColor(announcement.severity)}>
          {t(`announcements.severityLabels.${announcement.severity}`)}
        </Badge>
        <Text fontSize="sm" color="gray.500">
          v{announcement.version}
        </Text>
      </HStack>
    )
  }

  if (!canManage) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack
          justify={{ base: 'flex-start', md: 'space-between' }}
          align={{ base: 'stretch', md: 'flex-start' }}
          flexWrap="wrap"
          gap={3}
        >
          <Box flex={{ base: '1 1 100%', md: '1' }} minW={0}>
            <Heading size="lg" mb={2}>
              {t('announcements.title')}
            </Heading>
            <Text color="gray.600">
              {t('announcements.subtitle')}
            </Text>
          </Box>
          <Stack
            direction={{ base: 'column', sm: 'row' }}
            spacing={2}
            w={{ base: 'full', md: 'auto' }}
            align={{ base: 'stretch', sm: 'center' }}
          >
            <RefreshIconButton
              label={t('common.refresh')}
              tooltipPlacement="left"
              size="md"
              onClick={() => refetch()}
              isLoading={isFetching}
            />
            <Button colorScheme="blue" leftIcon={<FiPlus />} onClick={handleOpenCreate}>
              {t('announcements.create')}
            </Button>
          </Stack>
        </HStack>

        <DataTable<Announcement>
          data={listData?.items ?? []}
          loading={isLoading}
          error={error as Error | null}
          columns={columns}
          filters={statusOptions}
          onFiltersChange={handleFiltersChange}
          showFilterBar={true}
          pagination={paginationConfig}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          actions={getRowActions}
          actionsColumnWidth="6%"
          size="sm"
          emptyMessage={t('announcements.empty')}
        />
      </VStack>

      <Drawer isOpen={drawer.isOpen} placement="right" onClose={handleCloseDrawer} size="xl">
        <DrawerOverlay />
        <DrawerContent bg={drawerBg}>
          <DrawerCloseButton />
          <DrawerHeader>
            {drawerMode === 'create' ? t('announcements.create') : t('announcements.edit')}
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={6}>
              {drawerMode === 'edit' && (
                <Box>
                  {detailQuery.isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    handleStatusSummary(detailQuery.data)
                  )}
                </Box>
              )}

              <Box>
                <Heading size="sm" mb={3}>
                  {t('common.basicInfo')}
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isDisabled={isPublished || isReadOnly}>
                    <FormLabel>{t('announcements.fields.title')}</FormLabel>
                    <Input
                      value={formState.title}
                      onChange={handleFormChange('title')}
                      maxLength={ANNOUNCEMENT_TITLE_MAX_LENGTH}
                    />
                    <FormHelperText>
                      {formState.title.length}/{ANNOUNCEMENT_TITLE_MAX_LENGTH}
                    </FormHelperText>
                  </FormControl>
                  <FormControl isDisabled={isPublished || isReadOnly}>
                    <FormLabel>{t('announcements.fields.severity')}</FormLabel>
                    <DropdownSelect
                      value={formState.severity}
                      options={[
                        {
                          value: 'notice',
                          label: t('announcements.severityLabels.notice'),
                        },
                        {
                          value: 'urgent',
                          label: t('announcements.severityLabels.urgent'),
                        },
                      ]}
                      onChange={handleSeverityChange}
                      ariaLabel={t('announcements.fields.severity')}
                      isDisabled={isPublished || isReadOnly}
                    />
                  </FormControl>
                  <FormControl isDisabled={isPublished || isReadOnly}>
                    <FormLabel>{t('announcements.fields.channels')}</FormLabel>
                    <Stack direction="row" spacing={4}>
                      <Checkbox
                        isChecked={formState.channels.modal}
                        onChange={() => handleToggleChannel('modal')}
                      >
                        {t('announcements.channelLabels.modal')}
                      </Checkbox>
                      <Checkbox
                        isChecked={formState.channels.bar}
                        onChange={() => handleToggleChannel('bar')}
                      >
                        {t('announcements.channelLabels.bar')}
                      </Checkbox>
                      <Checkbox
                        isChecked={formState.channels.email}
                        onChange={() => handleToggleChannel('email')}
                      >
                        {t('announcements.channelLabels.email')}
                      </Checkbox>
                    </Stack>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              <Box>
                <Heading size="sm" mb={3}>
                  {t('announcements.fields.content')}
                </Heading>
                <FormControl isDisabled={isPublished || isReadOnly || !formState.channels.modal}>
                  <Textarea
                    value={formState.content}
                    onChange={handleFormChange('content')}
                    maxLength={ANNOUNCEMENT_CONTENT_MAX_LENGTH}
                    minH="120px"
                  />
                  <FormHelperText>
                    {formState.content.length}/{ANNOUNCEMENT_CONTENT_MAX_LENGTH}
                  </FormHelperText>
                </FormControl>
              </Box>

              <Divider />

              <Box>
                <HStack justify="space-between" mb={2}>
                  <Heading size="sm">{t('announcements.fields.barContent')}</Heading>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleAddSegment}
                    isDisabled={isReadOnly || isPublished || !formState.channels.bar}
                  >
                    {t('announcements.barContent.addSegment')}
                  </Button>
                </HStack>
                <FormControl
                  isDisabled={isReadOnly || isPublished || !formState.channels.bar}
                >
                  <FormHelperText mb={4}>
                    {totalBarLength}/{ANNOUNCEMENT_BAR_TOTAL_MAX_LENGTH}
                  </FormHelperText>
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={1} mb={2}>
                  <Text fontSize="xs" color="gray.500">
                    {t('announcements.barContent.segmentType')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {t('announcements.barContent.segmentText')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {t('announcements.barContent.segmentUrl')}
                  </Text>
                  <Text fontSize="xs" color="gray.500" textAlign="right">
                    {t('common.actions')}
                  </Text>
                </SimpleGrid>
                <VStack align="stretch" spacing={1}>
                  {formState.barSegments.map((segment, index) => (
                    <Box
                      key={`${segment.type}-${index}`}
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                    >
                      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={1}>
                        <FormControl
                          isDisabled={isReadOnly || isPublished || !formState.channels.bar}
                        >
                          <DropdownSelect
                            value={segment.type}
                            options={[
                              {
                                value: 'text',
                                label: t('announcements.barContent.textOption'),
                              },
                              {
                                value: 'link',
                                label: t('announcements.barContent.linkOption'),
                              },
                            ]}
                            onChange={value =>
                              handleSegmentChange(index, 'type', value)
                            }
                            size="sm"
                            ariaLabel={t('announcements.barContent.segmentType')}
                            isDisabled={
                              isReadOnly || isPublished || !formState.channels.bar
                            }
                          />
                        </FormControl>
                        <FormControl
                          isDisabled={isReadOnly || isPublished || !formState.channels.bar}
                        >
                          <Input
                            value={segment.text}
                            onChange={(event) =>
                              handleSegmentChange(index, 'text', event.target.value)
                            }
                            size="sm"
                            aria-label={t('announcements.barContent.segmentText')}
                          />
                        </FormControl>
                        <FormControl
                          isDisabled={
                            isReadOnly ||
                            isPublished ||
                            !formState.channels.bar ||
                            segment.type !== 'link'
                          }
                        >
                          <Input
                            value={segment.type === 'link' ? segment.url ?? '' : ''}
                            onChange={(event) =>
                              handleSegmentChange(index, 'url', event.target.value)
                            }
                            size="sm"
                            aria-label={t('announcements.barContent.segmentUrl')}
                          />
                        </FormControl>
                        <HStack justify="flex-end" align="center">
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => handleRemoveSegment(index)}
                            isDisabled={isReadOnly || isPublished || !formState.channels.bar}
                          >
                            {t('announcements.barContent.removeSegment')}
                          </Button>
                        </HStack>
                      </SimpleGrid>
                    </Box>
                  ))}
                </VStack>
              </Box>

              <Divider />

              <Box>
                <Heading size="sm" mb={3}>
                  {t('announcements.fields.ctaLabel')}
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isDisabled={isPublished || isReadOnly}>
                    <FormLabel>{t('announcements.fields.ctaLabel')}</FormLabel>
                    <Input value={formState.ctaLabel} onChange={handleFormChange('ctaLabel')} />
                  </FormControl>
                  <FormControl isDisabled={isPublished || isReadOnly}>
                    <FormLabel>{t('announcements.fields.ctaUrl')}</FormLabel>
                    <Input value={formState.ctaUrl} onChange={handleFormChange('ctaUrl')} />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Box>
                <Heading size="sm" mb={3}>
                  {t('announcements.fields.emailContentBlocks')}
                </Heading>
                <FormControl isDisabled={isPublished || isReadOnly || !formState.channels.email} mb={4}>
                  <FormLabel>{t('announcements.fields.emailSubtitle')}</FormLabel>
                  <Input
                    value={formState.emailSubtitle}
                    onChange={handleFormChange('emailSubtitle')}
                    maxLength={ANNOUNCEMENT_EMAIL_SUBTITLE_MAX_LENGTH}
                  />
                  <FormHelperText>
                    {formState.emailSubtitle.length}/{ANNOUNCEMENT_EMAIL_SUBTITLE_MAX_LENGTH}
                  </FormHelperText>
                </FormControl>
                <FormControl isDisabled={isPublished || isReadOnly || !formState.channels.email}>
                  <Textarea
                    value={formState.emailContentBlocks}
                    onChange={handleFormChange('emailContentBlocks')}
                    minH="140px"
                  />
                </FormControl>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={3}>
                  <Icon as={FiClock} />
                  <Heading size="sm">{t('announcements.fields.startsAt')}</Heading>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isDisabled={isPublished || isReadOnly}>
                    <FormLabel>{t('announcements.fields.startsAt')}</FormLabel>
                    <InputGroup size="sm">
                      <Input
                        type="datetime-local"
                        value={formState.startsAt}
                        onChange={handleFormChange('startsAt')}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={t('common.clear')}
                          icon={<FiX />}
                          size="xs"
                          variant="ghost"
                          onClick={() =>
                            setFormState((prev) => ({ ...prev, startsAt: '' }))
                          }
                          isDisabled={
                            isPublished || isReadOnly || !formState.startsAt
                          }
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormHelperText>
                      {formatUtcHint(formState.startsAt, t)}
                    </FormHelperText>
                  </FormControl>
                  <FormControl isDisabled={isReadOnly || isPublished}>
                    <FormLabel>{t('announcements.fields.endsAt')}</FormLabel>
                    <InputGroup size="sm">
                      <Input
                        type="datetime-local"
                        value={formState.endsAt}
                        onChange={handleFormChange('endsAt')}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={t('common.clear')}
                          icon={<FiX />}
                          size="xs"
                          variant="ghost"
                          onClick={() =>
                            setFormState((prev) => ({ ...prev, endsAt: '' }))
                          }
                          isDisabled={isReadOnly || isPublished || !formState.endsAt}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormHelperText>
                      {formatUtcHint(formState.endsAt, t)}
                    </FormHelperText>
                  </FormControl>
                </SimpleGrid>
                <FormControl isDisabled={isReadOnly || isPublished}>
                  <HStack spacing={3} mt={4}>
                    <Switch
                      isChecked={formState.schedule}
                      onChange={handleScheduleToggle}
                      isDisabled={isReadOnly || isPublished}
                    />
                    <Text fontSize="sm">{t('announcements.publish.schedule')}</Text>
                  </HStack>
                  <FormHelperText>{t('announcements.publish.scheduleHint')}</FormHelperText>
                </FormControl>
              </Box>

              {drawerMode === 'edit' && (
                <>
                  <Divider />
                  <Box>
                    <HStack justify="space-between" mb={3}>
                      <HStack spacing={2}>
                        <Icon as={FiZap} />
                        <Heading size="sm">{t('announcements.deliveries.title')}</Heading>
                      </HStack>
                      <RefreshIconButton
                        label={t('common.refresh')}
                        onClick={() => detailQuery.refetch()}
                        isLoading={detailQuery.isFetching}
                      />
                    </HStack>
                    {detailQuery.isLoading ? (
                      <Spinner size="sm" />
                    ) : detailQuery.data?.deliveries?.length ? (
                      <Box borderWidth="1px" borderRadius="md" overflowX="auto">
                        <Table size="sm" variant="simple">
                          <Thead>
                            <Tr>
                              <Th>{t('announcements.deliveries.channel')}</Th>
                              <Th>{t('announcements.deliveries.status')}</Th>
                              <Th>{t('announcements.deliveries.deliveredAt')}</Th>
                              <Th>{t('announcements.deliveries.error')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {detailQuery.data.deliveries.map((delivery) => (
                              <Tr key={`${delivery.channel}-${delivery.deliveredAt}`}>
                                <Td>{delivery.channel}</Td>
                                <Td>
                                  <Badge
                                    colorScheme={delivery.status === 'delivered' ? 'green' : 'red'}
                                  >
                                    {delivery.status}
                                  </Badge>
                                </Td>
                                <Td>
                                  {delivery.deliveredAt
                                    ? formatDateTime(delivery.deliveredAt)
                                    : '-'}
                                </Td>
                                <Td maxW="200px">
                                  <Text fontSize="xs" noOfLines={2}>
                                    {delivery.error ?? '-'}
                                  </Text>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {t('announcements.deliveries.empty')}
                      </Text>
                    )}
                  </Box>
                </>
              )}
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleCloseDrawer}>
                {t('common.cancel')}
              </Button>
              {drawerMode === 'create' && (
                <Button
                  colorScheme="blue"
                  onClick={handleCreate}
                  isLoading={createMutation.isPending}
                >
                  {t('announcements.actions.saveDraft')}
                </Button>
              )}
              {drawerMode === 'edit' && (
                <>
                  <Button
                    colorScheme="blue"
                    onClick={handleUpdate}
                    isLoading={updateMutation.isPending}
                    isDisabled={isReadOnly || isPublished}
                  >
                    {t('announcements.actions.update')}
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={() => handlePublish()}
                    isLoading={publishMutation.isPending}
                    isDisabled={isReadOnly || announcementStatus === 'published'}
                  >
                    {t('announcements.actions.publish')}
                  </Button>
                </>
              )}
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  )
}
